import React, { useCallback, useEffect, useMemo, useState } from 'react';
import AppIntersectionObserver from '../app/appIntersectionObserver';
import AppLoading from '../app/appLoading';
import { AppNoFeedback } from '../app/appNoFeedback';
import { Box, Button, Typography, Theme, useTheme } from '@mui/material';
import { useProjectsSummary } from '../../hooks/reports/useProjectsSummary';
import { ProjectsSummary, ReportFilters, ViewMode } from '../../models/reports';
import { AppRow } from '../../layout/appRow';
import { ColorPalette, TypographyFamily } from '../../styles/constants';
import WestIcon from '@mui/icons-material/West';
import { ReportsInputsData } from './reportsInputsData';
import { ReportsProjectDetails } from './reportsProjectDetails';
import { ReportsProjectItemMobile } from './reportsProjectItemMobile';
import { MyCoolGradient } from '../app/button/myCoolGradient';
import { SmallLayoutRouter } from './reportsPageSmallLayout';
import { DEFAULT_PAGE_SIZE } from '../../constants';

interface Props {
  filters: ReportFilters;
  viewUnloggedProjects: boolean;
  setRoute: (arg: SmallLayoutRouter) => void;
  viewMode: ViewMode;
}

const makeStyles = (theme: Theme) => ({
  topBox: { display: 'flex', flexDirection: 'column', width: '100%' },
  allocationsRow: {
    mb: 2,
  },
  allocationsBtn: {
    textDecorationColor: ColorPalette.GREEN,
    fontFamily: TypographyFamily.NEXA,
  },
  backBtnContent: { display: 'flex', alignItems: 'center', fontSize: 16 },
  mobileItemsRow: { display: 'flex', flexDirection: 'column' },
  mobileViewsBox: { pt: 2, pr: 1, pl: 1 },
  backIcon: {
    mr: 2,
    fill: `url(#my-cool-gradient) ${theme.palette.primary.main}`,
  },
});

export function ReportsProjectListMobile({
  filters,
  viewUnloggedProjects,
  setRoute,
  viewMode,
}: Props): JSX.Element {
  const theme = useTheme();
  const styles = makeStyles(theme);

  const [skip, setSkip] = useState(0);

  const { items, isLoading, fetchData, isEmpty, hasMore } =
    useProjectsSummary();

  useEffect(() => {
    fetchData(filters, viewUnloggedProjects);
  }, [filters, viewUnloggedProjects, fetchData]);

  const [selectedProject, setSelectedProject] =
    useState<ProjectsSummary | null>(null);

  const handleObserver = useCallback(() => {
    if (isLoading || !hasMore) {
      return;
    }

    fetchData(
      { ...filters, skip: skip + DEFAULT_PAGE_SIZE },
      viewUnloggedProjects
    );
    setSkip(skip + DEFAULT_PAGE_SIZE);
  }, [isLoading, hasMore, fetchData, filters, skip, viewUnloggedProjects]);

  return (
    <Box sx={styles.topBox}>
      <Box sx={styles.mobileViewsBox}>
        {!selectedProject ? (
          <>
            <AppRow sx={styles.allocationsRow}>
              <Button onClick={() => setRoute(SmallLayoutRouter.FILTERS)}>
                <Typography variant="h2" sx={styles.backBtnContent}>
                  <MyCoolGradient />
                  <WestIcon sx={styles.backIcon} />
                  <u style={styles.allocationsBtn}>Back</u>
                </Typography>
              </Button>
            </AppRow>
            <ReportsInputsData filters={filters} viewMode={viewMode} />
            <AppRow sx={styles.mobileItemsRow}>
              {items.map((item) => {
                return (
                  <ReportsProjectItemMobile
                    key={item.project.id}
                    setSelectedProject={setSelectedProject}
                    projectSummary={item}
                  />
                );
              })}
            </AppRow>
            <AppIntersectionObserver intersect={handleObserver} />
          </>
        ) : (
          <ReportsProjectDetails
            filters={filters}
            selectedProject={selectedProject}
            setRoute={setRoute}
            setSelectedProject={setSelectedProject}
          />
        )}
      </Box>

      {isLoading && <AppLoading />}
      {isEmpty && <AppNoFeedback info="data" />}
    </Box>
  );
}
